/**
 * CreatePipelineRenderer - Renderer for `create_pipeline` proposals.
 *
 * Renders a methodology switcher, editable stage list with probability column,
 * and read-only fields/roles preview in both `full` and `readonly` modes.
 *
 * The action payload is the flat, record-anchored `create_pipeline` shape:
 * pipeline/stage attributes are CRM property-value dicts keyed by property type
 * name (`ahoy_name`, `ahoy_probability` as a 0–1 fraction *string*, `ahoy_color`,
 * `ahoy_description`). A methodology `PipelineTemplate` carries the same shape
 * plus `key` / `description` metadata, so switching methodology is a pure
 * in-memory payload swap and editing round-trips through one shape.
 */

import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  InfoCircle,
} from '@untitledui/icons';
import {
  type FC,
  type Key,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
} from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { ConfirmationModal } from '@/lib/base/overlays/ConfirmationModal';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Popover } from '@/lib/base/overlays/popover';
import type {
  StageColumn,
  StageItem,
  StageValidationErrors,
} from '@/lib/crm/components/stages/StageListEditor';
import { StageListEditor } from '@/lib/crm/components/stages/StageListEditor';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { logger } from '@/lib/logger';
import { cx } from '@/utils/cx';

import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ActionPayload,
  CreatePipelinePayload,
  PipelinePropertyValues,
  PipelineTemplate,
  ProposedAction,
} from '../../../lib/proposals/core/types';
import { isCreatePipelinePayload } from '../../../lib/proposals/core/types';
import { usePipelineStagePersistence } from '../../../lib/proposals/hooks/usePipelineStagePersistence';
import type { ActionRendererProps } from '../../../lib/proposals/registry';
import { usePipelineTemplates } from '../../../lib/proposals/templates/usePipelineTemplates';
import { ActionError, ProposalContainer } from '../shared';

// =============================================================================
// Module-scope constants and helpers
// =============================================================================

const DEAL_STAGE_DEFAULT_PROBABILITY = 0.5;

/**
 * How long to wait after the last edit before writing the payload back.
 * Without this we issue one PUT per keystroke, and every response is fed back
 * through `onUpdate` as a new action — see the sync-guard note below.
 */
const PERSIST_DEBOUNCE_MS = 500;

/** Read a property-dict value as a string (values are stored serialization-native). */
function propString(props: PipelinePropertyValues, key: string): string {
  const value = props[key];
  if (typeof value === 'string') {
    return value;
  }
  return value == null ? '' : String(value);
}

/** Convert a stored probability fraction string ("0.25") to editor display (0–100). */
function probabilityToEditor(raw: unknown): number {
  const fraction =
    typeof raw === 'number' ? raw : parseFloat(String(raw ?? '0'));
  if (Number.isNaN(fraction)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(fraction * 100)));
}

/** Convert editor display value (0–100) back to a stored 0–1 fraction string. */
function editorToProbabilityString(p: number): string {
  return String(Math.max(0, Math.min(1, p / 100)));
}

/** Map one stored stage property-dict to an editor StageItem. */
function stageDictToItem(
  stage: PipelinePropertyValues,
  index: number
): StageItem {
  return {
    id: `stage-${index}`,
    name: propString(stage, 'ahoy_name'),
    color: typeof stage.ahoy_color === 'string' ? stage.ahoy_color : undefined,
    description: propString(stage, 'ahoy_description'),
    probability: probabilityToEditor(stage.ahoy_probability),
  };
}

/**
 * Whether this pipeline tracks win-probability — true when any stage carries an
 * `ahoy_probability` value. Deal pipelines do; a pipeline on a record type whose
 * stages have no probability property (e.g. notes, a custom type) does not, and
 * the probability UI is hidden for it. Gates both the editor columns and whether
 * `ahoy_probability` is written back on edit.
 */
function pipelineTracksProbability(payload: CreatePipelinePayload): boolean {
  return payload.stages_properties.some(
    s => s.ahoy_probability != null && s.ahoy_probability !== ''
  );
}

/**
 * Convert editor StageItem[] (0–100 probability) back to stored stage
 * property-dicts. `ahoy_order` is omitted — the backend sets it from list
 * position; `ahoy_color` is included only when set; `ahoy_probability` is
 * written only when the pipeline tracks it.
 */
function stagesToStageProperties(
  editorStages: StageItem[],
  tracksProbability: boolean
): PipelinePropertyValues[] {
  return editorStages.map(s => {
    const props: PipelinePropertyValues = {
      ahoy_name: s.name,
      ahoy_description: typeof s.description === 'string' ? s.description : '',
    };
    if (tracksProbability) {
      props.ahoy_probability = editorToProbabilityString(
        (s.probability as number) ?? 0
      );
    }
    if (typeof s.color === 'string') {
      props.ahoy_color = s.color;
    }
    return props;
  });
}

/**
 * Build an updated payload carrying a new stage list. Probability is preserved
 * only when the source pipeline already tracks it, so a probability-free
 * pipeline never gains the field through an edit.
 */
function withStages(
  payload: CreatePipelinePayload,
  stages: StageItem[]
): CreatePipelinePayload {
  return {
    ...payload,
    stages_properties: stagesToStageProperties(
      stages,
      pipelineTracksProbability(payload)
    ),
  };
}

/** Build a fresh create_pipeline payload from a methodology template. */
function payloadFromTemplate(t: PipelineTemplate): CreatePipelinePayload {
  return {
    record_entity_type_name: t.record_entity_type_name,
    pipeline_properties: { ...t.pipeline_properties },
    stages_properties: t.stages_properties.map(s => ({ ...s })),
    property_types: t.property_types,
    association_types: t.association_types,
  };
}

/**
 * System property types present on every record. Excluded from the "already on
 * your {record}" overlap list — they're plumbing, not fields the user thinks
 * about. Mirrors the skip set used by the pipeline card (GenericCardContent).
 */
const SYSTEM_FIELD_NAMES = new Set([
  'ahoy_name',
  'ahoy_display_name',
  'ahoy_created_at',
  'ahoy_updated_at',
  'ahoy_hidden',
]);

/**
 * One tile in the fields & relationships grid — icon and label, with the full
 * description behind a click-popover on the info icon (same DialogTrigger +
 * base Popover composition as EmailThreadVisibilityBadge / TimelineFilters).
 */
function TemplateSchemaTile({
  icon: Icon,
  label,
  description,
}: {
  icon: FC<{ className?: string }>;
  label: string;
  description?: string;
}) {
  return (
    <div className='flex items-center gap-2.5 px-4 py-2 min-w-0'>
      <Icon className='w-4 h-4 text-fg-tertiary shrink-0' />
      <p className='min-w-0 text-sm font-medium text-fg-primary truncate'>
        {label}
      </p>
      {description && (
        <DialogTrigger>
          <AriaButton
            aria-label={`About ${label}`}
            className={cx(
              'flex items-center justify-center w-5 h-5 shrink-0 rounded cursor-pointer',
              'text-fg-quaternary hover:text-fg-secondary',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
            )}
          >
            <InfoCircle className='w-3.5 h-3.5' />
          </AriaButton>
          <Popover size='sm' placement='bottom end' className='w-64! p-3'>
            <Dialog aria-label={`About ${label}`} className='outline-hidden'>
              <p className='text-sm text-fg-secondary'>{description}</p>
            </Dialog>
          </Popover>
        </DialogTrigger>
      )}
    </div>
  );
}

// Stage-description textarea sizing: grow to fit content (no clipping) but
// start at two lines. Mirrors TextAreaInput's scrollHeight auto-grow pattern.
const STAGE_DESCRIPTION_MIN_ROWS = 2;
const STAGE_DESCRIPTION_LINE_HEIGHT = 16; // text-xs line-height (1rem)

/** Inline-editable stage description, disclosed via the row's info toggle. */
function StageDescriptionEditor({
  stage,
  helpers,
}: {
  stage: StageItem;
  helpers: TemplateStageMutationHelpers;
}) {
  const description =
    typeof stage.description === 'string' ? stage.description : '';

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow so the full description is visible rather than clipped at two
  // rows. Reset to 'auto' first so the textarea can also shrink.
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    const minHeight =
      STAGE_DESCRIPTION_MIN_ROWS * STAGE_DESCRIPTION_LINE_HEIGHT;
    textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [description, adjustHeight]);

  const handleChange = useCallback(
    (value: string) => helpers.updateField(stage.id, 'description', value),
    [helpers, stage.id]
  );

  return (
    <TextArea
      ref={textareaRef}
      value={description}
      onChange={handleChange}
      rows={STAGE_DESCRIPTION_MIN_ROWS}
      placeholder='Add a description'
      aria-label={`Description for ${stage.name || 'stage'}`}
      resize='none'
      className='mt-1'
      wrapperClassName='bg-transparent shadow-none ring-0 focus-within:ring-0'
      textareaClassName='text-xs text-fg-tertiary overflow-hidden placeholder:text-fg-quaternary p-0'
    />
  );
}

// Short fit hints shown beside each methodology in the dropdown menu, so the
// choice isn't made blind. Mirrors the fit sentence in each template's yaml
// description (apps/pipelines/services/pipeline_templates/).
const METHODOLOGY_FIT_HINTS: Record<string, string> = {
  SPICED: 'SaaS sales',
  BANT: 'Transactional',
  MEDDIC: 'Enterprise',
};

interface TemplateStageMutationHelpers {
  updateField: (id: string, field: string, value: unknown) => void;
  markTouched: (id: string, field: string) => void;
}

function createTemplateStageColumns(
  helpers: TemplateStageMutationHelpers,
  includeProbability: boolean
): StageColumn<StageItem>[] {
  if (!includeProbability) {
    return [];
  }
  return [
    {
      id: 'progress',
      header: 'Win %',
      // Hidden on mobile — the editable input to the right still carries the value.
      width: 'hidden sm:block w-12',
      align: 'center',
      renderCell: stage => {
        const pct = (stage.probability as number) ?? 0;
        // Bar uses the stage's own color so the row carries one color system,
        // not a competing magnitude ramp.
        const color = typeof stage.color === 'string' ? stage.color : undefined;
        return (
          <div className='h-1.5 w-12 overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]'>
            <div
              className='h-full rounded-full bg-gray-300 transition-all'
              style={{ width: `${pct}%`, backgroundColor: color }}
            />
          </div>
        );
      },
    },
    {
      id: 'probability',
      header: '',
      width: 'w-16',
      renderCell: stage => {
        const pct = (stage.probability as number) ?? 0;
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const val = parseInt(e.target.value, 10);
          helpers.updateField(
            stage.id,
            'probability',
            isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
          );
        };
        const handleBlur = () => {
          helpers.markTouched(stage.id, 'probability');
        };
        return (
          <div className='relative w-16'>
            <input
              type='number'
              min='0'
              max='100'
              value={pct}
              onChange={handleChange}
              onBlur={handleBlur}
              className='w-full bg-[var(--color-bg-secondary)] text-xs font-medium text-[var(--color-text-secondary)] text-center border rounded-md py-1 px-1 pr-4 outline-none focus:ring-1 border-[var(--color-border-primary)] focus:ring-fg-brand-primary focus:border-fg-brand-primary'
            />
            <span className='absolute right-1.5 top-1 text-xs text-fg-quaternary pointer-events-none'>
              %
            </span>
          </div>
        );
      },
      renderDragPreview: stage => {
        const pct = (stage.probability as number) ?? 0;
        return <span className='text-xs text-fg-secondary'>{pct}%</span>;
      },
    },
  ];
}

/** Validate stage names — no empty names, no duplicates (case-insensitive). */
function validateStageNames(
  stages: StageItem[]
): Map<string, { name: string }> {
  const errors = new Map<string, { name: string }>();
  const seenNames = new Map<string, string>();
  for (const stage of stages) {
    if (!stage.name.trim()) {
      errors.set(stage.id, { name: 'Stage name cannot be empty' });
    } else {
      const lower = stage.name.trim().toLowerCase();
      const existing = seenNames.get(lower);
      if (existing !== undefined) {
        errors.set(stage.id, { name: `Duplicate stage name "${existing}"` });
      } else {
        seenNames.set(lower, stage.name.trim());
      }
    }
  }
  return errors;
}

// =============================================================================
// Main component
// =============================================================================

export function CreatePipelineRenderer({
  action,
  mode,
  className = '',
  onUpdate,
  proposalId,
  orgId,
}: ActionRendererProps) {
  // ── Payload validation — derived before hooks ─────────────────────────────
  const validPayload = isCreatePipelinePayload(action.payload);
  const payload = validPayload
    ? (action.payload as unknown as CreatePipelinePayload)
    : null;

  // ── Derived data ──────────────────────────────────────────────────────────
  const recordType = payload?.record_entity_type_name ?? '';
  const pipelineName = payload
    ? propString(payload.pipeline_properties, 'ahoy_name')
    : '';
  const propertyTypes = useMemo(() => payload?.property_types ?? [], [payload]);
  const associationTypes = useMemo(
    () => payload?.association_types ?? [],
    [payload]
  );

  // Depend on `stages_properties`, not the whole payload — otherwise any
  // unrelated payload change re-derives the array and trips the sync effect.
  const editorStages = useMemo<StageItem[]>(
    () => payload?.stages_properties.map(stageDictToItem) ?? [],
    [payload?.stages_properties]
  );

  // Show the win-probability column only when this pipeline actually tracks it
  // (deal stages carry ahoy_probability; notes / custom types may not).
  const tracksProbability = !!payload && pipelineTracksProbability(payload);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const { templates, error: listError } = usePipelineTemplates(
    orgId,
    recordType || undefined
  );

  // Existing schema for this record type — used to show the user what fields and
  // relationships they ALREADY have, so the additions below are seen in context
  // (and overlaps are obvious). Both hooks are cache-shared and self-disable
  // when their key is absent (empty recordType / orgId).
  const { schema: existingPropertySchema } = usePropertySchema(
    orgId ?? 0,
    recordType
  );
  const { associationTypes: existingAssociationTypes } =
    useAssociationTypes(orgId);

  // The payload no longer carries a methodology key — match it back to a fetched
  // template by pipeline name so the switcher can show/select the current one.
  const matchedTemplate = useMemo(
    () =>
      templates.find(
        t => propString(t.pipeline_properties, 'ahoy_name') === pipelineName
      ) ?? null,
    [templates, pipelineName]
  );
  const displayKey = matchedTemplate?.key ?? pipelineName;

  // ── Proposal client ───────────────────────────────────────────────────────
  const proposalClient = useMemo(
    () => (orgId ? createProposalClient(orgId) : null),
    [orgId]
  );

  // Each successful update_payload mints a NEW action version (new id); the
  // old id 404s on the next save. Track the current id and adopt the server's
  // returned action after every save.
  const actionIdRef = useRef(action.id);
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [stages, setStages] = useState<StageItem[]>(editorStages);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  // Template switch pending confirmation (set when the current pipeline is
  // custom — switching would replace it).
  const [pendingSwitchKey, setPendingSwitchKey] = useState<string | null>(null);
  // True once the user edits any stage; flips the switcher to "Custom" and gates
  // the switch confirmation.
  const [stagesDirty, setStagesDirty] = useState(false);

  // Normalize the mount-time payload through the editor pipeline so change
  // detection can suppress round-tripped saves (stagesToStageProperties always
  // adds ahoy_description; raw server payload may not have it).
  const initialSavedValueRef = useRef<CreatePipelinePayload | null>(
    payload
      ? withStages(payload, payload.stages_properties.map(stageDictToItem))
      : null
  );

  // "Custom" = the pipeline diverges from every preset: either the user has
  // edited it, or the incoming payload matches no methodology (e.g. an
  // agent-authored pipeline). The switcher shows "Custom" and switching to a
  // preset warns the custom pipeline will be replaced.
  const isCustom = !matchedTemplate || stagesDirty;
  const triggerLabel = isCustom ? 'Custom' : displayKey;
  const displayDescription = isCustom
    ? undefined
    : matchedTemplate?.description;

  // ── Persist payload to backend ────────────────────────────────────────────
  const persistFn = useCallback(
    async (updatedPayload: CreatePipelinePayload) => {
      if (!proposalClient || !proposalId) {
        return;
      }
      const updatedAction = await proposalClient.updateActionPayload(
        proposalId,
        actionIdRef.current,
        {
          payload: updatedPayload as unknown as ActionPayload,
        }
      );
      actionIdRef.current = updatedAction.id;
      onUpdate?.(updatedAction);
    },
    [proposalClient, proposalId, onUpdate]
  );

  // Captured once: every successful save mints a NEW action id, and a
  // coordination registration keyed on the live id would re-register (and
  // momentarily drop off the approval flush rail) after every save.
  const coordinationIdRef = useRef(`create-pipeline-${action.id}`);

  const {
    notifyEdit,
    flushPersist,
    apiError,
    isGuardActive,
    retryNow,
    syncSavedValue,
  } = usePipelineStagePersistence<CreatePipelinePayload>(persistFn, {
    debounceMs: PERSIST_DEBOUNCE_MS,
    errorMessage: "We couldn't save the pipeline changes. Try again.",
    logContext: { proposalId, actionId: actionIdRef.current },
    coordinationId: coordinationIdRef.current,
    initialSavedValue: initialSavedValueRef.current ?? undefined,
  });
  // Mirrored into a ref so the sync effect below can read the LATEST guard
  // value without depending on it reactively — the effect must only re-run
  // when `editorStages` itself changes (matching the pre-extraction
  // dependency array), not whenever the guard happens to flip.
  const isGuardActiveRef = useRef(isGuardActive);
  isGuardActiveRef.current = isGuardActive;

  // ── Sync stages when the action prop (or payload) changes ─────────────────
  //
  // Every save calls `onUpdate` with the action the server returned, which
  // comes back down as a new `payload` and re-derives `editorStages`. That
  // payload reflects the stages as of when the save STARTED, so adopting it
  // while the user is still typing rewinds the field to an older value — the
  // characters they typed in between are dropped, which reads as text being
  // deleted or scrambled mid-word. Hold local state until the server has
  // acknowledged every edit. Same guard as the board's inline rename
  // (`lib/crm/pipeline/components/PipelineColumn.tsx`).
  useEffect(() => {
    if (isGuardActiveRef.current) {
      return;
    }
    // Advance the change-detection baseline to whatever we're adopting. After a
    // local save this is a no-op (the baseline already equals the saved value),
    // but when the payload arrived from an external update (WebSocket/refetch)
    // this keeps the baseline in step so a later revert to it is not written.
    const currentPayload = payloadRef.current;
    if (currentPayload) {
      syncSavedValue(withStages(currentPayload, editorStages));
    }
    setStages(editorStages);
  }, [editorStages, syncSavedValue]);

  // ── Notify parent of update + persist ────────────────────────────────────
  const notifyPayloadUpdate = useCallback(
    (updatedPayload: CreatePipelinePayload) => {
      // Use the latest action id (tracked in actionIdRef), not the prop's
      // possibly-stale value. After a save, the prop's `action.id` may not yet
      // reflect the server's new versioned id; using it for the optimistic
      // update would propagate a stale id to the parent and, on a subsequent
      // edit before the prop catches up, the api call would target the old
      // (now-404) action.
      onUpdate?.({
        ...action,
        id: actionIdRef.current,
        payload: updatedPayload as unknown as ActionPayload,
      } as ProposedAction);
      notifyEdit(updatedPayload);
    },
    [action, onUpdate, notifyEdit]
  );

  // ── Ref indirection — keeps column factory and updateField dep-arrays stable
  const stagesRef = useRef(stages);
  stagesRef.current = stages;
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const notifyPayloadUpdateRef = useRef(notifyPayloadUpdate);
  notifyPayloadUpdateRef.current = notifyPayloadUpdate;

  // ── Mark a field as touched (shows its validation error) ─────────────────
  const markTouched = useCallback((id: string, field: string) => {
    setTouchedFields(prev => {
      const key = `${id}:${field}`;
      if (prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // ── Generic field update — used by column factory via ref indirection ─────
  const updateField = useCallback(
    (id: string, field: string, value: unknown) => {
      setStagesDirty(true);
      const updated = stagesRef.current.map(s =>
        s.id === id ? { ...s, [field]: value } : s
      );
      setStages(updated);
      const currentPayload = payloadRef.current;
      if (currentPayload) {
        notifyPayloadUpdateRef.current(withStages(currentPayload, updated));
      }
    },
    []
  );

  const mutationHelpers = useMemo<TemplateStageMutationHelpers>(
    () => ({ updateField, markTouched }),
    [updateField, markTouched]
  );

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = useMemo<StageColumn<StageItem>[]>(
    () => createTemplateStageColumns(mutationHelpers, tracksProbability),
    [mutationHelpers, tracksProbability]
  );

  // ── Disclosed, inline-editable stage description ──────────────────────────
  const renderRowDetail = useCallback(
    (stage: StageItem) => (
      <StageDescriptionEditor stage={stage} helpers={mutationHelpers} />
    ),
    [mutationHelpers]
  );

  // ── Validation ────────────────────────────────────────────────────────────
  const allValidationErrors = useMemo<StageValidationErrors>(() => {
    const nameErrors = validateStageNames(stages);
    const merged: StageValidationErrors = {};
    for (const stage of stages) {
      const nameErr = nameErrors.get(stage.id);
      const pct = (stage.probability as number) ?? 0;
      const probErr =
        pct < 0 || pct > 100 ? { probability: 'Must be 0–100%' } : null;
      if (nameErr || probErr) {
        merged[stage.id] = { ...nameErr, ...probErr };
      }
    }
    return merged;
  }, [stages]);

  const visibleErrors = useMemo<StageValidationErrors>(() => {
    const filtered: StageValidationErrors = {};
    for (const [id, errs] of Object.entries(allValidationErrors)) {
      const visibleFieldErrors: Record<string, string | undefined> = {};
      let hasVisible = false;
      for (const [field, msg] of Object.entries(errs)) {
        if (touchedFields.has(`${id}:${field}`)) {
          visibleFieldErrors[field] = msg;
          hasVisible = true;
        }
      }
      if (hasVisible) {
        filtered[id] = visibleFieldErrors;
      }
    }
    return filtered;
  }, [allValidationErrors, touchedFields]);

  // ── Stage edit handlers ───────────────────────────────────────────────────
  const handleNameChange = useCallback(
    (id: string, name: string) => {
      setStagesDirty(true);
      const updated = stages.map(s => (s.id === id ? { ...s, name } : s));
      setStages(updated);
      if (payload) {
        notifyPayloadUpdate(withStages(payload, updated));
      }
    },
    [stages, payload, notifyPayloadUpdate]
  );

  const handleNameBlur = useCallback(
    (id: string) => {
      markTouched(id, 'name');
      // Don't make the user wait out the debounce once they've left the field.
      flushPersist();
    },
    [markTouched, flushPersist]
  );

  const handleColorChange = useCallback(
    (id: string, color: string | null) => {
      setStagesDirty(true);
      const updated = stages.map(s =>
        s.id === id ? { ...s, color: color ?? undefined } : s
      );
      setStages(updated);
      if (payload) {
        notifyPayloadUpdate(withStages(payload, updated));
      }
    },
    [stages, payload, notifyPayloadUpdate]
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (stages.length <= 2) {
        return;
      }
      setStagesDirty(true);
      const updated = stages.filter(s => s.id !== id);
      setStages(updated);
      if (payload) {
        notifyPayloadUpdate(withStages(payload, updated));
      }
    },
    [stages, payload, notifyPayloadUpdate]
  );

  const handleAdd = useCallback(() => {
    setStagesDirty(true);
    const newStage: StageItem = {
      id: `stage-${Date.now()}`,
      name: '',
      probability: probabilityToEditor(DEAL_STAGE_DEFAULT_PROBABILITY),
    };
    const updated = [...stages, newStage];
    setStages(updated);
    if (payload) {
      notifyPayloadUpdate(withStages(payload, updated));
    }
  }, [stages, payload, notifyPayloadUpdate]);

  const handleReorder = useCallback(
    (reordered: StageItem[]) => {
      setStagesDirty(true);
      setStages(reordered);
      if (payload) {
        notifyPayloadUpdate(withStages(payload, reordered));
      }
    },
    [payload, notifyPayloadUpdate]
  );

  const canDelete = useCallback(() => stages.length > 2, [stages.length]);

  // ── Methodology switch ────────────────────────────────────────────────────
  // The list endpoint returns full templates (stages, fields, roles), so
  // switching is a pure in-memory swap — no per-key fetch.
  const applySwitch = useCallback(
    (key: string) => {
      const next = templates.find(t => t.key === key);
      if (!next) {
        logger.error('Unknown pipeline methodology selected', undefined, {
          key,
        });
        return;
      }
      const nextPayload = payloadFromTemplate(next);
      setStagesDirty(false);
      // Apply the swap to local state directly rather than waiting for the
      // save to round-trip back through the sync effect above — that effect
      // deliberately ignores incoming payloads while edits are outstanding.
      setStages(nextPayload.stages_properties.map(stageDictToItem));
      notifyPayloadUpdate(nextPayload);
    },
    [templates, notifyPayloadUpdate]
  );

  const handleSwitchMethodology = useCallback(
    (key: Key | null) => {
      if (typeof key !== 'string') {
        return;
      }
      // A clean (non-custom) pipeline ignores re-selecting its own preset; a
      // custom one always re-applies (e.g. restoring SPICED after edits).
      if (!isCustom && key === displayKey) {
        return;
      }
      // A custom pipeline (edited or agent-authored) would be replaced — confirm.
      if (isCustom) {
        setPendingSwitchKey(key);
        return;
      }
      applySwitch(key);
    },
    [isCustom, displayKey, applySwitch]
  );

  const handleConfirmSwitch = useCallback(() => {
    if (pendingSwitchKey !== null) {
      applySwitch(pendingSwitchKey);
    }
    setPendingSwitchKey(null);
  }, [pendingSwitchKey, applySwitch]);

  const handleCancelSwitch = useCallback(() => {
    setPendingSwitchKey(null);
  }, []);

  // Dropdown items — key (acronym) only; the description renders below.
  const methodologyItems = useMemo(
    () =>
      templates.map(t => ({
        id: t.key,
        label: t.key,
        addon: METHODOLOGY_FIT_HINTS[t.key],
      })),
    [templates]
  );

  // Controlled selection for the methodology menu — nothing is selected while
  // the pipeline is custom (no preset is the "current" one).
  const selectedMethodologyKeys = useMemo(
    () =>
      new Set<string>(
        !isCustom && matchedTemplate ? [matchedTemplate.key] : []
      ),
    [isCustom, matchedTemplate]
  );

  const recordLabel = recordType ? getTypeLabelByName(recordType) : 'record';
  const recordLabelLower = recordLabel.toLowerCase();
  // Plural label resolved from the entity type's own schema/registry (not a
  // string-interpolated "+s"), so it reads correctly for every type
  // (Deal → Deals, Company → Companies, custom types via their schema label).
  const recordLabelPluralLower = (
    recordType ? getTypeLabelByName(recordType, { plural: true }) : 'records'
  ).toLowerCase();
  const hasTemplates = templates.length > 0;

  // Fields & relationships this pipeline ADDS — new property types (field-type
  // icon) then new associations (target-entity icon).
  const schemaGridItems = useMemo(() => {
    if (!payload) {
      return [];
    }
    return [
      ...propertyTypes.map(pt => ({
        key: `prop:${pt.property_type_name}`,
        label: pt.label,
        description: pt.description ?? undefined,
        Icon: getPropertyTypeIconComponent(pt),
      })),
      ...associationTypes.map(at => ({
        key: `assoc:${at.association_type_name}`,
        label: at.label,
        description: `Links the ${recordLabelLower} to a ${getTypeLabelByName(at.target_entity_type_name).toLowerCase()}.`,
        Icon: getAssociationTargetIconComponent(at),
      })),
    ];
  }, [payload, propertyTypes, associationTypes, recordLabelLower]);

  // Fields & relationships the record ALREADY has — only surfaced when this
  // pipeline introduces new fields, so the user sees their existing schema as
  // context for what's being added. Existing fields exclude system plumbing;
  // existing associations are matched to the SAME target types as the suggested
  // associations, so the list highlights overlap rather than the whole graph.
  const existingSchemaItems = useMemo(() => {
    if (!payload || propertyTypes.length === 0) {
      return [];
    }
    const suggestedTargets = new Set(
      associationTypes.map(at => at.target_entity_type_name)
    );
    const fieldItems = Object.values(existingPropertySchema)
      // Only fields the user can actually read — skip system plumbing and any
      // internal-only property (value_read_access !== 'all').
      .filter(
        pt =>
          !SYSTEM_FIELD_NAMES.has(pt.property_type_name) &&
          pt.value_read_access === 'all'
      )
      .map(pt => ({
        key: `existing-prop:${pt.property_type_name}`,
        label: pt.label,
        description: pt.description || undefined,
        Icon: getPropertyTypeIconComponent(pt),
      }));
    const assocItems = existingAssociationTypes
      .filter(
        at =>
          at.source_entity_type_name === recordType &&
          at.value_read_access !== 'internal' &&
          suggestedTargets.has(at.target_entity_type_name)
      )
      .map(at => ({
        key: `existing-assoc:${at.association_type_name}`,
        label: at.label,
        description: `Links the ${recordLabelLower} to a ${getTypeLabelByName(at.target_entity_type_name).toLowerCase()}.`,
        Icon: getAssociationTargetIconComponent(at),
      }));
    return [...fieldItems, ...assocItems];
  }, [
    payload,
    propertyTypes,
    associationTypes,
    existingPropertySchema,
    existingAssociationTypes,
    recordType,
    recordLabelLower,
  ]);

  // ── Early return — AFTER all hooks ───────────────────────────────────────
  if (!validPayload || !payload) {
    return (
      <ActionError message='Invalid create pipeline payload' mode={mode} />
    );
  }

  // ── Readonly mode ─────────────────────────────────────────────────────────
  if (mode === 'readonly') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div>
          <p className='text-sm font-medium text-fg-primary'>{displayKey}</p>
          {displayDescription && (
            <p className='text-sm text-fg-secondary'>{displayDescription}</p>
          )}
        </div>

        {/* Stages */}
        <div className='space-y-1.5'>
          {stages.map((stage, i) => (
            <div
              key={stage.id}
              className='flex items-center gap-3 px-3 py-2 bg-bg-secondary rounded-lg'
            >
              <span className='flex items-center justify-center w-5 h-5 rounded-full bg-utility-success-100 text-[10px] font-semibold text-utility-success-700 shrink-0'>
                {i + 1}
              </span>
              <CheckCircle className='w-4 h-4 text-utility-success-600 shrink-0' />
              <span className='flex-1 text-sm font-medium text-fg-primary'>
                {stage.name}
              </span>
            </div>
          ))}
        </div>

        {/* Fields */}
        {propertyTypes.length > 0 && (
          <div className='space-y-1.5'>
            <p className='text-xs font-semibold text-fg-tertiary uppercase tracking-wide'>
              Fields
            </p>
            <ul className='space-y-1'>
              {propertyTypes.map(pt => (
                <li
                  key={pt.property_type_name}
                  className='text-sm text-fg-primary'
                >
                  {pt.label}{' '}
                  <span className='text-fg-quaternary'>
                    ({pt.field_type_name})
                  </span>
                  {pt.description && (
                    <span className='block text-xs text-fg-tertiary'>
                      {pt.description}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Roles */}
        {associationTypes.length > 0 && (
          <div className='space-y-1.5'>
            <p className='text-xs font-semibold text-fg-tertiary uppercase tracking-wide'>
              Roles
            </p>
            <ul className='space-y-1'>
              {associationTypes.map(at => (
                <li
                  key={at.association_type_name}
                  className='text-sm text-fg-primary'
                >
                  {at.label} → {at.target_entity_type_name}{' '}
                  <span className='text-fg-quaternary'>({at.cardinality})</span>
                  {at.reverse_label && (
                    <span className='block text-xs text-fg-tertiary'>
                      {at.reverse_label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // ── Full mode ─────────────────────────────────────────────────────────────
  if (mode === 'full') {
    const hasVisibleErrors = Object.keys(visibleErrors).length > 0;

    return (
      <ProposalContainer className={className}>
        {/* Header — always rendered with a fixed-height row so nothing snaps
            in or shifts the content below. The methodology select only appears
            once templates have loaded (record types without presets, e.g.
            notes, never show it). The description below always persists and
            swaps to the selected methodology's blurb once one is active. */}
        <div className='rounded-lg border border-secondary bg-primary'>
          <div className='grid min-h-9 grid-cols-[auto_1fr] items-center gap-x-3 py-1.5 pl-4 pr-2'>
            {/* Match the dropdown button's py-2 so the row keeps the same
                height whether or not the select is present (no snap). */}
            <span className='py-2 text-sm font-semibold text-fg-primary whitespace-nowrap'>
              {hasTemplates
                ? `Choose ${recordLabelLower} pipeline template`
                : `Configure your new ${recordLabelLower} pipeline`}
            </span>
            {hasTemplates && (
              <div className='flex justify-end min-w-0'>
                <Dropdown.Root>
                  <Button
                    color='tertiary'
                    size='sm'
                    iconTrailing={ChevronDown}
                    aria-label='Pipeline template'
                  >
                    {triggerLabel}
                  </Button>
                  <Dropdown.Popover placement='bottom end'>
                    <Dropdown.Menu
                      items={methodologyItems}
                      selectedKeys={selectedMethodologyKeys}
                      onAction={handleSwitchMethodology}
                    >
                      {item => (
                        <Dropdown.Item
                          id={item.id}
                          label={item.label}
                          addon={item.addon}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.Root>
              </div>
            )}
          </div>
        </div>

        <p className='px-4 text-sm text-fg-secondary'>
          {displayDescription ??
            `Set up the stages your ${recordLabelLower} pipeline moves through.`}
        </p>

        {listError && (
          <small className='text-error-600'>Could not load methodologies</small>
        )}

        {/* Existing fields & relationships — overlap context. Only shown when
            this pipeline adds new fields, so the user sees what they already
            have alongside the additions below. */}
        {existingSchemaItems.length > 0 && (
          <p className='px-4 text-xs font-medium text-fg-quaternary uppercase tracking-wider'>
            Fields already on your {recordLabelPluralLower}
          </p>
        )}
        {existingSchemaItems.length > 0 && (
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2'>
            {existingSchemaItems.map(item => (
              <TemplateSchemaTile
                key={item.key}
                icon={item.Icon}
                label={item.label}
                description={item.description}
              />
            ))}
          </div>
        )}

        {/* Fields & relationships grid */}
        {schemaGridItems.length > 0 && (
          <p className='px-4 text-xs font-medium text-fg-quaternary uppercase tracking-wider'>
            Fields this adds to your {recordLabelPluralLower}
          </p>
        )}
        {schemaGridItems.length > 0 && (
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2'>
            {schemaGridItems.map(item => (
              <TemplateSchemaTile
                key={item.key}
                icon={item.Icon}
                label={item.label}
                description={item.description}
              />
            ))}
          </div>
        )}

        {/* API error banner */}
        {apiError && (
          <div className='flex items-center justify-between gap-3 rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
            <p className='text-sm text-utility-error-700'>{apiError}</p>
            <Button color='secondary' size='sm' onClick={retryNow}>
              Retry
            </Button>
          </div>
        )}

        {/* Validation errors banner */}
        {hasVisibleErrors && (
          <div className='flex items-start gap-2.5 px-3.5 py-2.5 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
            <AlertCircle className='w-4 h-4 text-utility-error-600 shrink-0 mt-0.5' />
            <p className='text-sm text-utility-error-700'>
              Please fix validation errors before proceeding.
            </p>
          </div>
        )}

        {/* Stage list editor */}
        <StageListEditor
          stages={stages}
          columns={columns}
          renderRowDetail={renderRowDetail}
          bordered
          showColumnHeaders
          nameHeader={`Stage · ${stages.length}`}
          onNameChange={handleNameChange}
          onNameBlur={handleNameBlur}
          onColorChange={handleColorChange}
          onRemove={handleRemove}
          onReorder={handleReorder}
          onAdd={handleAdd}
          canDelete={canDelete}
          validationErrors={visibleErrors}
        />

        {/* Reversibility reassurance — defuses schema-commitment anxiety */}
        <p className='text-xs text-fg-tertiary'>
          You can change stages and fields anytime in settings.
        </p>

        {/* Confirm before a template switch replaces a custom pipeline */}
        <ConfirmationModal
          isOpen={pendingSwitchKey !== null}
          title='Replace custom pipeline?'
          description={`Switching to ${pendingSwitchKey ?? ''} replaces your custom pipeline with its template. Your custom changes will be lost.`}
          confirmLabel='Replace'
          cancelLabel='Keep my pipeline'
          variant='warning'
          instance='switch-methodology'
          onConfirm={handleConfirmSwitch}
          onCancel={handleCancelSwitch}
        />
      </ProposalContainer>
    );
  }

  // ── Unsupported mode fallback ─────────────────────────────────────────────
  return <span>Create pipeline (mode: {mode})</span>;
}
